import React from 'react'
import { COLORS, ICONS, ICON_SCALE } from '../globals'
import styled from 'styled-components'
import { VizElement } from 'components/VizElement'

const Container = styled.div`
  margin-top: 2em;
`
const Table = styled.table`
  table-layout: fixed;
  width: 100%;
`
const VizColumn = styled.td`
  width: 180px;
  @media only screen and (max-width: 750px) {
    width: 110px;
  }
  text-align: center;
  position: relative;
  &::after {
    content: ' ';
    border-left: 1.5px solid rgba(0, 0, 0, 0.7);
    display: inline-block;
    height: 36%;
    position: absolute;
    left: 50%;
    top: 0;
    @media only screen and (max-width: 750px) {
      height: 35%;
    }
  }
`
const Label = styled.td`
  font-family: var(--chart-font-stack);
  width: calc(50% - 80px);
  vertical-align: middle;
  font-size: 15px;
  font-weight: 400;
  border-bottom: 1px solid #eee;
  line-height: 1.5em;
  height: 30%;
  padding: 0.8em 0;
  &.first {
    border: 0;
    text-align: center;
  }
  &.left {
    text-align: right;
    padding-right: 0.5em;
    width: 35%;
    @media only screen and (max-width: 750px) {
      width: 27%;
    }
  }
  &.right {
    padding-left: 0.5em;
    text-align: left;
    width: 35%;
    @media only screen and (max-width: 750px) {
      width: 27%;
    }
  }
  &.bottom {
    vertical-align: middle;
    border: 0;
  }
  &.middle {
    vertical-align: middle;
  }
  &.top {
    vertical-align: middle;
  }
  &.center {
    width: 30%;
    @media only screen and (max-width: 750px) {
      width: 46%;
    }
  }
  @media only screen and (max-width: 750px) {
    font-size: 12px;
  }
`
const posMob = { w: 140, h: 120, x: 70, y: 60, r: 22 }
const posDesk = { w: 180, h: 160, x: 90, y: 80, r: 25 }

const VizSVG = styled.svg`
  width: ${posDesk.w}px;
  height: ${posDesk.h}px;
  @media only screen and (max-width: 750px) {
    width: ${posMob.w}px;
    height: ${posMob.h}px;
  }
`
const NameCol = styled.td`
  font-family: var(--chart-font-stack);
  text-transform: uppercase;
  text-align: center;
  font-size: 15px;
`
const NameSec = styled.div`
  font-family: var(--chart-font-stack);
  text-transform: uppercase;
  font-weight: 200;
  text-align: center;
  font-size: 15px;
`

export const KeyTable = ({
  data,
  locales,
  colorLabels,
  isMobile
}: {
  data: vizElementDataType[]
  locales: { [K in indicatorTypes]: string }
  colorLabels: string[]
  isMobile: boolean
}) => {
  return (
    <Container>
      <Table>
        <tbody>
          <tr>
            <Label className='left' />
            <Label
              className='first center'
              dangerouslySetInnerHTML={{ __html: locales.excessmort }}
            />
            <Label className='right' />
          </tr>
          <tr>
            <Label
              className='left top'
              dangerouslySetInnerHTML={{ __html: locales.vaccdays }}
            />
            <VizColumn rowSpan={3}>
              <VizSVG>
                <VizElement
                  x={isMobile ? posMob.x : posDesk.x}
                  y={isMobile ? posMob.y : posDesk.y}
                  colors={data[0].colors}
                  rad={isMobile ? posMob.r : posDesk.r}
                />
              </VizSVG>
            </VizColumn>
            <Label
              className='right top'
              dangerouslySetInnerHTML={{ __html: locales.unemploy }}
            />
          </tr>
          <tr>
            <Label
              className='left middle'
              dangerouslySetInnerHTML={{ __html: locales.tests }}
            />
            <Label
              className='right middle'
              dangerouslySetInnerHTML={{ __html: locales.kof }}
            />
          </tr>
          <tr>
            <Label
              className='left bottom'
              dangerouslySetInnerHTML={{ __html: locales.positivity }}
            />
            <Label
              className='right bottom'
              dangerouslySetInnerHTML={{ __html: locales.icu }}
            />
          </tr>
          <tr>
            <NameCol
              colSpan={3}
              style={{ paddingBottom: '2em', fontWeight: 700 }}
            >
              {data[0].label}
            </NameCol>
          </tr>
          <tr>
            <td colSpan={3} style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-block', marginRight: '20px' }}>
                <Viz cols={data[1].colors} isMobile={isMobile} />
                <NameSec> {data[1].label}</NameSec>
              </div>
              <div style={{ display: 'inline-block' }}>
                <Viz cols={data[2].colors} isMobile={isMobile} />
                <NameSec> {data[2].label}</NameSec>
              </div>
            </td>
          </tr>
          <ColorKeyElement colorLabels={colorLabels} />
        </tbody>
      </Table>
    </Container>
  )
}
const Viz = ({
  cols,
  isMobile
}: {
  cols: { [k in indicatorTypes]: string }
  isMobile: boolean
}) => {
  return (
    <VizSVG>
      <VizElement
        x={isMobile ? posMob.x : posDesk.x}
        y={isMobile ? posMob.y : posDesk.y}
        colors={cols}
        rad={isMobile ? posMob.r : posDesk.r}
      />
    </VizSVG>
  )
}

const ColorKeyElement = ({ colorLabels }: { colorLabels: string[] }) => {
  const cols = JSON.parse(JSON.stringify(COLORS)).reverse()
  return (
    <>
      <tr>
        <td colSpan={3} style={{ height: '30px' }}></td>
      </tr>
      <ColorKeyRow>
        <td colSpan={3}>
          <div className='wrapper'>
            <div className='left'>{colorLabels[0]}</div>
            <div className='center'>
              <ColorKey>
                {cols.map(m => (
                  <Swatch color={m} />
                ))}
              </ColorKey>
            </div>
            <div className='right'>{colorLabels[1]}</div>
          </div>
        </td>
      </ColorKeyRow>
    </>
  )
}
const ColorKeyRow = styled.tr`
  td {
    border-top: 1px solid #eee;
    font-family: var(--chart-font-stack);
    font-size: 15px;
    @media only screen and (max-width: 750px) {
      font-size: 12px;
    }
    div {
      line-height: 1.2em;
      display: inline-block;
      &.wrapper {
        display: flex;
        padding-top: 20px;
        padding-bottom: 40px;
      }
      &.center {
        text-align: center;
        width: 140px;
      }
      &.left {
        padding-right: 10px;

        text-align: right;
        flex: 0 0 calc(50% - 70px);
      }
      &.right {
        padding-left: 10px;
        text-align: left !important;
        flex: 0 0 calc(50% - 70px);
      }
    }
  }
`

const ColorKey = styled.div`
  display: flex !important;
  justify-content: space-around;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`
const Swatch = styled.div<{ color: string }>`
  display: inline-block;
  width: 14px !important;
  height: 14px !important;
  border-radius: 8px;
  margin-top: 0px;
  @media only screen and (max-width: 750px) {
    margin-top: 0px;
  }
  background-color: ${props => props.color};
`
